import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { BrandMark } from '../components/layout/BrandMark';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authClient, signIn } from '../lib/auth-client';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

/**
 * Query string from the OAuth authorize endpoint when an MCP client (e.g.
 * Claude) sent the user here to sign in; null for a normal login.
 */
function getOAuthQuery(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.has('client_id') && params.has('response_type') ? window.location.search : null;
}

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthQuery] = useState(getOAuthQuery);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (oauthQuery) {
        // The server answers this sign-in with a redirect to the MCP client.
        // fetch can't follow that cross-origin, so don't follow it: check the
        // session, then restart authorize as a full-page navigation.
        await signIn.email({ email, password }, { redirect: 'manual' });
        const session = await authClient.getSession();
        if (session.data?.session) {
          window.location.href = `${import.meta.env.VITE_API_URL ?? ''}/api/auth/mcp/authorize${oauthQuery}`;
          return;
        }
        setError('Felaktig e-postadress eller lösenord.');
        return;
      }

      const result = await signIn.email({ email, password });
      if (result.error) {
        setError('Felaktig e-postadress eller lösenord.');
      } else {
        void navigate({ to: '/' });
      }
    } catch {
      setError('Något gick fel. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <BrandMark />
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-7">
          <h1 className="text-2xl font-semibold tracking-tight">Logga in</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {oauthQuery
              ? 'Logga in för att ge en AI-assistent (t.ex. Claude) åtkomst till din Budgie.'
              : 'Välkommen tillbaka.'}
          </p>
          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
            <div className="space-y-2">
              <Label htmlFor="login-email">E-postadress</Label>
              <Input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="h-10"
                placeholder="din@email.se"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="login-password">Lösenord</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  Glömt lösenordet?
                </Link>
              </div>
              <Input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="h-10"
              />
            </div>
            {error && (
              <p
                role="alert"
                className="rounded-lg bg-negative-soft px-3 py-2 text-sm text-negative"
              >
                {error}
              </p>
            )}
            <Button type="submit" disabled={loading} className="mt-1 w-full" size="lg">
              {loading ? 'Loggar in…' : 'Logga in'}
            </Button>
          </form>
        </div>
        <p className="mt-5 text-center text-sm text-muted-foreground">
          Inget konto?{' '}
          <Link to="/register" className="font-medium text-primary hover:underline">
            Skapa konto
          </Link>
        </p>
        <p className="mt-6 flex justify-center gap-4 text-xs text-muted-foreground">
          <Link to="/privacy" className="hover:text-foreground">
            Integritetspolicy
          </Link>
          <Link to="/terms" className="hover:text-foreground">
            Användarvillkor
          </Link>
        </p>
      </div>
    </div>
  );
}
